import React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { cva, type VariantProps } from "class-variance-authority";
import { RemiErrorDeprecated } from "@remi/error";
import { RnChevronRight } from "@remi/icons/next";

import { cn } from "../lib/utils";

const RemiCollapsible = CollapsiblePrimitive.Root;

interface RemiCollapsibleTriggerProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleTrigger> {
  carrot?: boolean;
  carrotSize?: "xs" | "sm" | "md";
  carrotClassName?: string;
  asChild?: boolean;
}

type CloneableChildProps = {
  className?: string;
  children?: React.ReactNode;
};

function assertCloneableElement<P extends CloneableChildProps>(
  node: React.ReactNode,
): asserts node is React.ReactElement<P> {
  if (!React.isValidElement<P>(node)) {
    throw new RemiErrorDeprecated({
      message: "RemiCollapsibleTrigger with asChild must have a valid React element as its child.",
    });
  }
}

const RemiCollapsibleTrigger = ({
  ref,
  className,
  carrot = false,
  carrotSize = "sm",
  carrotClassName,
  children,
  asChild,
  ...props
}: RemiCollapsibleTriggerProps & {
  ref?: React.Ref<React.ComponentRef<typeof CollapsiblePrimitive.CollapsibleTrigger>>;
}) => {
  const carrotElement = carrot ? (
    <RnChevronRight
      className={cn("carrot rotate-0 text-icon-tertiary transition-all duration-200", carrotClassName)}
      size={carrotSize}
    />
  ) : null;

  if (asChild) {
    const child = React.Children.only(children);
    assertCloneableElement(child);

    const childWithCarrot = React.cloneElement(child, {
      className: cn(child.props.className, "data-[state=open]:[&_.carrot]:rotate-90", className),
      children: (
        <>
          {child.props.children}
          {carrotElement}
        </>
      ),
    });

    return (
      <CollapsiblePrimitive.CollapsibleTrigger ref={ref} asChild {...props}>
        {childWithCarrot}
      </CollapsiblePrimitive.CollapsibleTrigger>
    );
  }

  return (
    <CollapsiblePrimitive.CollapsibleTrigger
      ref={ref}
      {...props}
      className={cn(
        "flex w-full flex-row items-center justify-start gap-3 data-[state=open]:[&_.carrot]:rotate-90",
        className,
      )}
    >
      {children}
      {carrotElement}
    </CollapsiblePrimitive.CollapsibleTrigger>
  );
};

const collapsibleVariants = cva("overflow-hidden", {
  variants: {
    transitionDuration: {
      none: "",
      default:
        "data-[state=closed]:animate-collapsible-close-default data-[state=open]:animate-collapsible-open-default",
      fast: "data-[state=closed]:animate-collapsible-close-fast data-[state=open]:animate-collapsible-open-fast",
    },
  },
  defaultVariants: {
    transitionDuration: "default",
  },
});

interface RemiCollapsibleContentProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>,
    VariantProps<typeof collapsibleVariants> {}

const RemiCollapsibleContent = ({
  ref,
  className,
  transitionDuration,
  ...props
}: RemiCollapsibleContentProps & {
  ref?: React.Ref<React.ComponentRef<typeof CollapsiblePrimitive.CollapsibleContent>>;
}) => {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      ref={ref}
      {...props}
      className={cn(collapsibleVariants({ transitionDuration }), className)}
    />
  );
};

RemiCollapsibleContent.displayName = "RemiCollapsibleContent";

export { RemiCollapsible, RemiCollapsibleContent, RemiCollapsibleTrigger };
